import React from 'react';

import {
  useExposeRemoveMenuFavoritesExperiment,
  useRemoveMenuFavoritesExperiment,
} from 'experiments/use-remove-menu-favorites-experiment';

interface IFavoriteActionGateProps {
  children: React.ReactNode;
  shouldDisplay?: boolean;
  /**
   * Whether this render should count as exposure to the remove menu favorites experiment.
   * Defaults to `shouldDisplay`. Pass this separately when a surface must stay mounted
   * (to preserve its internal state) even when it is not currently eligible for exposure,
   * e.g. an offer entry that temporarily hides its favorite action.
   */
  shouldExpose?: boolean;
}

/**
 * This component is used to gate the display of a component based on the remove menu favorites experiment.
 */
export const FavoriteActionGate = ({
  children,
  shouldDisplay = true,
  shouldExpose = shouldDisplay,
}: IFavoriteActionGateProps) => {
  const removeMenuFavorites = useRemoveMenuFavoritesExperiment();

  useExposeRemoveMenuFavoritesExperiment(shouldExpose);

  // removeMenuFavorites is `undefined` while the assignment is still unresolved; treat that like
  // treatment (hidden) so this never renders a guess that a moment later gets corrected.
  if (!shouldDisplay || removeMenuFavorites !== false) {
    return null;
  }

  return <>{children}</>;
};

/**
 * Wraps a component so it only renders when the remove menu favorites experiment is off.
 */
export function withFavoriteActionGate<P extends object>(
  Component: React.ComponentType<P>,
  getShouldDisplay?: (props: P) => boolean,
  getShouldExpose?: (props: P) => boolean
): React.FC<P> {
  return props => {
    const shouldDisplay = getShouldDisplay ? getShouldDisplay(props) : true;
    const shouldExpose = getShouldExpose ? getShouldExpose(props) : shouldDisplay;

    return (
      <FavoriteActionGate shouldDisplay={shouldDisplay} shouldExpose={shouldExpose}>
        <Component {...props} />
      </FavoriteActionGate>
    );
  };
}
